import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Box,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import AuthContext from '../../context/authContext';
import { currencyFormatter, percentFormatter, decimalFormatter } from '../../util/formatHelper';
import useStatisticJob from '../../hooks/useStatisticJob';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import StatisticViewShell from '../ui/StatisticViewShell';
import { STATISTIC_TYPE_B2B } from '../../constants/statisticTypes';
import { isFeatureEnabled } from '../../config/featureFlags';
import StaffCostEstimateNotice from '../ui/StaffCostEstimateNotice';
import {
  getCartesianGridStroke,
  getRechartsAxisStroke,
  getRechartsTooltipContentStyle,
  getRechartsTooltipLabelStyle,
  getStatisticsChartCursorFill,
  getStatisticsKpiHelperColor,
  getStatisticsKpiLabelColor,
  getStatisticsSurfaceBackground,
  getStatisticsTableRowBorderTop,
  getRechartsLegendBarProps,
  getStatisticsHtmlThStyle,
} from '../../util/chartTheme';

const defaultStartDate = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
const defaultEndDate = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
const DEFAULT_EMPLOYER_COST_FACTOR = '1.3';

const formatCurrency = value => currencyFormatter.format(value ?? 0);
const formatHours = value => `${decimalFormatter.format(value ?? 0)} h`;
const formatPercent = value =>
  value === null || value === undefined ? '–' : `${percentFormatter.format(value)} %`;

const B2bStatistics = () => {
  const theme = useTheme();
  const chartGridStroke = useMemo(() => getCartesianGridStroke(theme), [theme]);
  const chartTooltipContentStyle = useMemo(() => getRechartsTooltipContentStyle(theme), [theme]);
  const statSurfaceBg = useMemo(() => getStatisticsSurfaceBackground(theme), [theme]);
  const chartCursorFill = useMemo(() => getStatisticsChartCursorFill(theme), [theme]);
  const axisStroke = useMemo(() => getRechartsAxisStroke(theme), [theme]);
  const tooltipLabelStyle = useMemo(() => getRechartsTooltipLabelStyle(theme), [theme]);
  const chartLegendBarProps = useMemo(() => getRechartsLegendBarProps(theme), [theme]);
  const htmlThStyle = useCallback((extra = {}) => getStatisticsHtmlThStyle(theme, extra), [theme]);
  const tableRowBorderTop = useMemo(() => getStatisticsTableRowBorderTop(theme), [theme]);
  const kpiLabelColor = useMemo(() => getStatisticsKpiLabelColor(theme), [theme]);
  const kpiHelperColor = useMemo(() => getStatisticsKpiHelperColor(theme), [theme]);
  const revenueColor = theme.palette.primary.main;
  const costColor = theme.palette.error.main;
  const ratioColor = theme.palette.warning.main;

  const cardStyle = useMemo(
    () => ({
      background: statSurfaceBg,
      borderRadius: '8px',
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
    }),
    [statSurfaceBg]
  );

  const authCtx = useContext(AuthContext);
  const canView =
    authCtx.checkRight('statistic:read') ||
    authCtx.checkRight('statistic:special:staffCost') ||
    authCtx.checkRight('statistic:read:b2b');

  const employerCostFactorRef = useRef(DEFAULT_EMPLOYER_COST_FACTOR);
  const [startDate, setStartDate] = useState(dayjs(defaultStartDate));
  const [endDate, setEndDate] = useState(dayjs(defaultEndDate));
  const [chartType, setChartType] = useState('bar');
  const [selectedCacheEntryId, setSelectedCacheEntryId] = useState(null);

  const [appliedFilter, setAppliedFilter] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    employerCostFactor: DEFAULT_EMPLOYER_COST_FACTOR,
  });

  const applyFilters = (nextStart, nextEnd) => {
    const s = nextStart || startDate;
    const e = nextEnd || endDate;
    if (!s || !e || !s.isValid() || !e.isValid()) {
      toast.error('Bitte wähle gültige Daten aus');
      return;
    }
    if (s.isAfter(e)) {
      toast.error('Das Startdatum darf nicht nach dem Enddatum liegen');
      return;
    }
    const rawFactorInput = employerCostFactorRef.current ? employerCostFactorRef.current.value : '';
    const rawFactor = Number(String(rawFactorInput).replace(',', '.'));
    const employerCostFactor =
      Number.isFinite(rawFactor) && rawFactor > 0 ? String(rawFactor) : DEFAULT_EMPLOYER_COST_FACTOR;
    setSelectedCacheEntryId(null);
    setAppliedFilter({
      startDate: s.format('YYYY-MM-DD'),
      endDate: e.format('YYYY-MM-DD'),
      employerCostFactor,
    });
  };

  const filterParams = useMemo(() => {
    if (!canView) return null;
    return {
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      employerCostFactor: appliedFilter.employerCostFactor,
    };
  }, [appliedFilter, canView]);

  const job = useStatisticJob({
    statisticType: STATISTIC_TYPE_B2B,
    endpoint: '/api/v1/statistic/b2b',
    filterParams,
  });
  const stats = job.payload;

  useEffect(() => {
    if (job.status === 'failed' && job.error) {
      toast.error(`Fehler beim Laden der B2B-Statistik: ${job.error}`);
    }
  }, [job.status, job.error]);

  const { entries: cacheEntries, isLoading: cacheEntriesLoading } = useStatisticCacheEntries({
    statisticType: STATISTIC_TYPE_B2B,
  });

  const handleSelectCacheEntry = useCallback(
    entryId => {
      setSelectedCacheEntryId(entryId);
      if (entryId) job.loadCacheEntry(entryId);
      else job.fetch();
    },
    [job]
  );

  useEffect(() => {
    setStartDate(dayjs(appliedFilter.startDate));
    setEndDate(dayjs(appliedFilter.endDate));
  }, [appliedFilter]);

  const chartData = useMemo(
    () =>
      (stats?.timeSeries?.daily || []).map(p => ({
        ...p,
        label: dayjs(p.date).format('DD.MM.'),
      })),
    [stats]
  );

  const kpiCards = useMemo(() => {
    if (!stats) return [];
    const b = stats.bookings || {};
    const c = stats.staffCost || {};
    return [
      { label: 'B2B-Einbuchungen', value: b.count ?? 0, helper: 'Buchungen durch das B2B-Team im Zeitraum' },
      { label: 'Buchungswert', value: formatCurrency(b.revenue), helper: 'Summe der B2B-Einbuchungen' },
      { label: 'Ø Buchungswert', value: formatCurrency(b.averageValue), helper: 'Durchschnitt je B2B-Buchung' },
      { label: 'Personen', value: b.participants ?? 0, helper: 'Teilnehmer auf B2B-Buchungen' },
      {
        label: 'B2B-Personalkosten',
        value: formatCurrency(c.total),
        helper: `${formatHours(c.hours)} · inkl. Faktor ×${stats?.meta?.employerCostFactor ?? ''}`,
      },
      {
        label: 'Personalkostenquote',
        value: formatPercent(stats.costRatioPercent),
        helper: 'B2B-Personalkosten / Buchungswert',
      },
      {
        label: 'Umsatz / Stunde',
        value: stats.revenuePerHour === null ? '–' : formatCurrency(stats.revenuePerHour),
        helper: 'Buchungswert je B2B-Personalstunde',
      },
    ];
  }, [stats]);

  if (!canView) return <Navigate to='/welcome' replace />;
  // Hidden behind a feature flag: available on staging, not on live.
  if (!isFeatureEnabled('b2bStatistics')) return <Navigate to='/welcome' replace />;

  const employerCostFactorFilter = (
    <TextField
      fullWidth
      type='number'
      label='Arbeitgeber-Faktor'
      defaultValue={DEFAULT_EMPLOYER_COST_FACTOR}
      inputRef={employerCostFactorRef}
      inputProps={{ step: '0.05', min: '1', max: '5' }}
    />
  );

  const renderKpiCards = () => {
    if (!stats) return null;
    return (
      <section>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {kpiCards.map(card => (
            <Box
              key={card.label}
              sx={{ ...cardStyle, flex: '1 1 220px', minWidth: 0, maxWidth: '100%' }}
            >
              <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>{card.label}</span>
              <strong style={{ fontSize: '1.5rem', overflowWrap: 'anywhere' }}>{card.value}</strong>
              <small style={{ color: kpiHelperColor }}>{card.helper}</small>
            </Box>
          ))}
        </Box>
      </section>
    );
  };

  const renderChart = () => {
    if (!stats || !chartData.length) return null;
    return (
      <section>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent='space-between'
          sx={{ mb: 1 }}
        >
          <Typography variant='h6' component='h2' sx={{ m: 0 }}>
            B2B-Einbuchungen &amp; Personalkosten
          </Typography>
          <ToggleButtonGroup
            value={chartType}
            exclusive
            size='small'
            onChange={(_, value) => value && setChartType(value)}
          >
            <ToggleButton value='bar'>Wert &amp; Kosten</ToggleButton>
            <ToggleButton value='count'>Anzahl</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Paper elevation={3} sx={{ p: 2, background: statSurfaceBg, height: 380, minWidth: 0 }}>
          <ResponsiveContainer width='100%' height='100%'>
            <ComposedChart data={chartData}>
              <CartesianGrid stroke={chartGridStroke} vertical={false} />
              <XAxis dataKey='label' stroke={axisStroke} />
              <YAxis
                yAxisId='euro'
                stroke={axisStroke}
                tickFormatter={value => currencyFormatter.format(value)}
              />
              <YAxis yAxisId='right' orientation='right' stroke={axisStroke} />
              <Tooltip
                cursor={{ fill: chartCursorFill }}
                contentStyle={chartTooltipContentStyle}
                labelStyle={tooltipLabelStyle}
                formatter={(value, name) =>
                  name === 'Anzahl Buchungen'
                    ? value
                    : name === 'Quote'
                      ? formatPercent(value)
                      : formatCurrency(value)
                }
              />
              <Legend {...chartLegendBarProps} />
              {chartType === 'bar' ? (
                <>
                  <Bar yAxisId='euro' dataKey='bookingRevenue' name='Buchungswert' fill={revenueColor} />
                  <Bar yAxisId='euro' dataKey='staffCost' name='Personalkosten' fill={costColor} />
                  <Line
                    yAxisId='right'
                    type='monotone'
                    dataKey='costRatioPercent'
                    name='Quote'
                    stroke={ratioColor}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                </>
              ) : (
                <Bar yAxisId='right' dataKey='bookingCount' name='Anzahl Buchungen' fill={revenueColor} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </Paper>
      </section>
    );
  };

  const renderCreatorsTable = () => {
    if (!stats?.topCreators?.length) return null;
    return (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
          Einbuchungen nach Mitarbeiter (B2B-Team)
        </Typography>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '480px' }}>
            <thead>
              <tr>
                <th style={htmlThStyle()}>Mitarbeiter</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Buchungen</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Buchungswert</th>
              </tr>
            </thead>
            <tbody>
              {stats.topCreators.map(c => (
                <tr key={c.key}>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>{c.displayName}</td>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop, textAlign: 'right' }}>
                    {c.bookingCount}
                  </td>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop, textAlign: 'right' }}>
                    {formatCurrency(c.bookingRevenue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  const renderMethodNote = () => {
    if (!stats?.meta) return null;
    const { employerCostFactor, b2bTeamSize } = stats.meta;
    return (
      <Paper
        elevation={0}
        sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', background: statSurfaceBg }}
      >
        <Typography variant='body2' color='text.secondary'>
          „Einbuchungen durch B2B" = interne Buchungen, deren Ersteller ({b2bTeamSize} Mitarbeiter mit
          B2B-Rolle) sie im Zeitraum angelegt hat (nach Erstelldatum). Personalkosten = B2B-Arbeitszeiten ×
          Stundenlohn × Arbeitgeber-Faktor (×{employerCostFactor}). Die Quote setzt B2B-Personalkosten
          ins Verhältnis zum Buchungswert.
        </Typography>
      </Paper>
    );
  };

  return (
    <StatisticViewShell
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      branchSelectIdPrefix='b2b-branch'
      extraFilters={[employerCostFactorFilter]}
      onApply={applyFilters}
      status={job.status}
      isStale={job.isStale}
      cacheComputedAt={job.cacheComputedAt}
      cacheExpiresAt={job.cacheExpiresAt}
      job={job.job}
      error={job.error}
      cacheEntries={cacheEntries}
      selectedEntryId={selectedCacheEntryId}
      onSelectEntry={handleSelectCacheEntry}
      entriesLoading={cacheEntriesLoading}
      onRefresh={job.refresh}
      hasPayload={!!stats}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'clip',
          '& > *': { minWidth: 0, maxWidth: '100%', mb: 3 },
          '& > *:last-child': { mb: 0 },
        }}
      >
        <StaffCostEstimateNotice assumptions={stats?.meta?.assumptions} sx={{ mb: 1 }} />
        {renderKpiCards()}
        {renderChart()}
        {renderCreatorsTable()}
        {renderMethodNote()}
      </Box>
    </StatisticViewShell>
  );
};

export default B2bStatistics;
